import { useEffect, useState } from 'react';
import { useCustomMutation } from '@refinedev/core';
import { Link2, Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { notify } from '@/lib/notify';
import { UserSearchCombobox } from '@/components/shared';
import type { User } from '@/resources/users/types';
import type { UserInvitation } from './types';

interface AttachInvitationToUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invitation: UserInvitation | null;
  onAttached: () => void;
}

interface ErrorWithMessage {
  message: string;
}

function getErrorMessage(value: unknown, fallback: string): string {
  if (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof (value as ErrorWithMessage).message === 'string' &&
    (value as ErrorWithMessage).message.length > 0
  ) {
    return (value as ErrorWithMessage).message;
  }
  return fallback;
}

/**
 * Диалог ручной привязки приглашения к существующему пользователю.
 *
 * Кейс: автоматический accept не сработал — например, EMAIL-инвайт получателю,
 * который зарегистрировался через Telegram, не открывая письмо. Админ выбирает
 * юзера через combobox-поиск, бэк выдаёт items+tags и переводит статус в ACCEPTED.
 */
export function AttachInvitationToUserDialog({
  open,
  onOpenChange,
  invitation,
  onAttached,
}: AttachInvitationToUserDialogProps): React.ReactElement {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedUser(null);
    }
  }, [open]);

  const attachMutation = useCustomMutation();
  const attach = attachMutation.mutate;
  const isSubmitting = attachMutation.mutation.isPending;

  const handleSubmit = (): void => {
    if (!selectedUser || !invitation || isSubmitting) {
      return;
    }

    attach(
      {
        url: `/user-invitations/${invitation.id}/attach-user`,
        method: 'post',
        values: { userId: selectedUser.id },
      },
      {
        onSuccess: (response) => {
          const data = response.data as {
            accepted: boolean;
            reason: 'ACCEPTED' | 'ALREADY_ACCEPTED' | 'EXPIRED';
          };
          if (data.accepted) {
            notify.success(
              `Приглашение #${invitation.id} принято для пользователя #${selectedUser.id}`,
            );
          } else if (data.reason === 'ALREADY_ACCEPTED') {
            notify.info('Приглашение уже было принято ранее');
          } else if (data.reason === 'EXPIRED') {
            notify.error('Приглашение истекло — привязка невозможна');
          }
          onAttached();
          onOpenChange(false);
        },
        onError: (error) => {
          notify.error(getErrorMessage(error, 'Не удалось привязать приглашение'));
        },
      },
    );
  };

  const recipientHint = ((): string => {
    if (!invitation) {
      return '';
    }
    if (invitation.email) {
      return `EMAIL → ${invitation.email}`;
    }
    if (invitation.telegramUserId) {
      const name =
        [invitation.telegramFirstName, invitation.telegramLastName]
          .filter(Boolean)
          .join(' ')
          .trim() ||
        (invitation.telegramUsername ? `@${invitation.telegramUsername}` : '') ||
        `tg:${invitation.telegramUserId}`;
      return `TELEGRAM → ${name}`;
    }
    return '';
  })();

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Привязать приглашение к пользователю
          </DialogTitle>
          <DialogDescription>
            Приглашение будет принято от имени админа: выдадутся доступы и теги,
            статус перейдёт в ACCEPTED.
            {invitation ? (
              <>
                <br />
                <span className="text-xs text-muted-foreground">
                  Приглашение #{invitation.id} · {recipientHint}
                </span>
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Пользователь</Label>
            <UserSearchCombobox
              value={selectedUser}
              onChange={setSelectedUser}
              disabled={isSubmitting}
              placeholder="Найдите пользователя по имени, email, телеграму…"
            />
          </div>

          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
            Действие принимает приглашение от имени админа — проверка получателя
            (email/telegramId match) не выполняется. Убедитесь, что выбран
            правильный пользователь.
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedUser || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Привязываем…
              </>
            ) : (
              'Привязать и принять'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
